import { createTheme, alpha } from "@mui/material/styles";
import "@mui/x-date-pickers/themeAugmentation";
import AppConfig from "@app/util/AppConfig";
const getDesignTokens = (mode) => {
    // Define palette first so we can reference it in components
    const palette = {
        mode,
        primary: {
            main: "#00A9DF",
            dark: "#008bb7",
            light: alpha("#00A9DF", 0.15),
            border: alpha("#00A9DF", 0.3),
            hover: alpha("#00A9DF", 0.5),
        },
        secondary: {
            // 6% = 0F | 10% = 1A
            main: mode === "light" ? "#F0F1F1" : "#2F2F2F",
            contrastText: mode === "light" ? "#5B5B5B" : "#FFFFFF",
            // 15% = 26 | 5% = 0D
            dark: mode === "light" ? "#DFDFDF" : "#484848",
        },
        error: {
            main: "#E71A46",
            // 6% = 0F
            light: mode === "light" ? "#FF00370F" : "#E71A460F",
        },
        warning: {
            main: mode === "light" ? "#F8C600" : "#F8C600",
            contrastText: mode === "light" ? "#F57C00" : "#F5A700",
            // 11% = 1C | 6% = 0F
            light: mode === "light" ? "#F8C6001C" : "#F5A7000F",
        },
        success: {
            main: "#08D780",
            dark: mode === "light" ? "#04c88e" : "#02b17d",
            light: alpha("#08D780", 0.08),
        },
        info: {
            main: "#00A9DF",
            light: alpha("#00A9DF", 0.08),
        },
        text: {
            primary: mode === "light" ? "#212121" : "#ffffff",
            secondary: mode === "light" ? "#5B5B5B" : "#acacac",
            subtitle: mode === "light" ? "#989898" : "#9F9F9F",
        },
        common: {
            black: mode === "light" ? "#000000" : "#000000",
            white: mode === "light" ? "#ffffff" : "#ffffff",
        },
        // Custom app colors with light/dark variants
        appColors: {
            pageBg: mode === "light" ? "#F5F5F5" : "#121212",
            cardBg: mode === "light" ? "#ffffff" : "#1C1C1C",
            cardBg2: mode === "light" ? "#F0F1F1" : "#2F2F2F",
            primaryFg: mode === "light" ? "#212121" : "#FFFFFF",
            secondaryFg: mode === "light" ? "#5B5B5B" : "#acacac",
            tertiaryFg: mode === "light" ? "#989898" : "#9F9F9F",
            quaternaryFg: mode === "light" ? "#F5F5F5" : "#545454",
            border: mode === "light" ? "#D2D2D280" : "#37373780",
            border2: mode === "light" ? "#C4C4C4" : "#525252",
            border3: mode === "light" ? "#E8E8E8" : "#262626",
        },
        // --- NEW CUSTOM PALETTE EXTENSION START (Green, Blue, Orange) ---
        gray: {
            main: "#9CA3AF",
            light: alpha("#9CA3AF", 0.15),
            border: alpha("#9CA3AF", 0.3),
            hover: alpha("#9CA3AF", 0.5),
        },
        purple: {
            main: "#8746f7",
            light: alpha("#8746f7", 0.15),
            border: alpha("#8746f7", 0.3),
            hover: alpha("#8746f7", 0.5),
        },
        yellow: {
            main: "#F7BA00",
            light: alpha("#F7BA00", 0.15),
            border: alpha("#F7BA00", 0.3),
            hover: alpha("#F7BA00", 0.5),
        },
        orange: {
            main: "#EA580C",
            light: alpha("#EA580C", 0.15),
            border: alpha("#EA580C", 0.3),
            hover: alpha("#EA580C", 0.5),
        },
        teal: {
            main: "#0D9488",
            light: alpha("#0D9488", 0.15),
            border: alpha("#0D9488", 0.3),
            hover: alpha("#0D9488", 0.5),
        },
        green: {
            main: "#16A34A",
            light: alpha("#16A34A", 0.15),
            border: alpha("#16A34A", 0.3),
            hover: alpha("#16A34A", 0.5),
        },
        slate: {
            main: "#64748B",
            light: alpha("#64748B", 0.15),
            border: alpha("#64748B", 0.3),
            hover: alpha("#64748B", 0.5),
        },
        indigo: {
            main: "#4F46E5",
            light: alpha("#4F46E5", 0.15),
            border: alpha("#4F46E5", 0.3),
            hover: alpha("#4F46E5", 0.5),
        },
        pink: {
            main: "#DB2777",
            light: alpha("#DB2777", 0.15),
            border: alpha("#DB2777", 0.3),
            hover: alpha("#DB2777", 0.5),
        },
        cyan: {
            main: "#3B82F6",
            light: alpha("#3B82F6", 0.15),
            border: alpha("#3B82F6", 0.3),
            hover: alpha("#3B82F6", 0.5),
        },
        // ---------------------------------------------------------------
    };
    return {
        palette,
        spacing: 4,
    };
};
// Create a function that returns theme with all customizations
const createThemeWithComponents = (mode) => {
    const designTokens = getDesignTokens(mode);
    const { palette } = designTokens;
    return createTheme(Object.assign(Object.assign({}, designTokens), { shape: {
            borderRadius: 8,
        }, shadows: [
            "none",
            "0px 2px 4px rgba(0, 0, 0, 0.05)",
            "0px 4px 8px rgba(0, 0, 0, 0.08)",
            "0px 6px 12px rgba(0, 0, 0, 0.1)",
            "0px 8px 16px rgba(0, 0, 0, 0.12)",
            "0px 12px 24px rgba(0, 0, 0, 0.14)",
            "0px 16px 32px rgba(0, 0, 0, 0.16)",
            "0px 2px 4px rgba(0, 0, 0, 0.05)",
            "0px 4px 8px rgba(0, 0, 0, 0.08)",
            "0px 6px 12px rgba(0, 0, 0, 0.1)",
            "0px 8px 16px rgba(0, 0, 0, 0.12)",
            "0px 12px 24px rgba(0, 0, 0, 0.14)",
            "0px 16px 32px rgba(0, 0, 0, 0.16)",
            "0px 2px 4px rgba(0, 0, 0, 0.05)",
            "0px 4px 8px rgba(0, 0, 0, 0.08)",
            "0px 6px 12px rgba(0, 0, 0, 0.1)",
            "0px 8px 16px rgba(0, 0, 0, 0.12)",
            "0px 12px 24px rgba(0, 0, 0, 0.14)",
            "0px 16px 32px rgba(0, 0, 0, 0.16)",
            "0px 2px 4px rgba(0, 0, 0, 0.05)",
            "0px 4px 8px rgba(0, 0, 0, 0.08)",
            "0px 6px 12px rgba(0, 0, 0, 0.1)",
            "0px 8px 16px rgba(0, 0, 0, 0.12)",
            "0px 12px 24px rgba(0, 0, 0, 0.14)",
            "0px 16px 32px rgba(0, 0, 0, 0.16)",
        ], transitions: {
            duration: {
                shortest: 150,
                shorter: 200,
                short: 250,
                standard: 300,
                complex: 375,
                enteringScreen: 225,
                leavingScreen: 195,
            },
        }, components: {
            MuiCssBaseline: {
                styleOverrides: {
                    a: {
                        color: palette.primary.main,
                        textDecoration: "none",
                        "&:hover": {
                            textDecoration: "underline",
                        },
                    },
                    "@keyframes fadeIn": {
                        from: { opacity: 0 },
                        to: { opacity: 1 },
                    },
                    "@keyframes slideUp": {
                        from: { opacity: 0, transform: "translateY(10px)" },
                        to: { opacity: 1, transform: "translateY(0)" },
                    },
                },
            },
            MuiLink: {
                styleOverrides: {
                    root: {
                        color: palette.primary.main,
                        textDecoration: "none",
                        "&:hover": {
                            textDecoration: "underline",
                        },
                    },
                },
            },
            MuiButton: {
                // defaultProps: {
                //   variant: "contained",
                // },
                styleOverrides: {
                    root: {
                        textTransform: "none",
                        borderRadius: 8,
                        padding: "4px 14px",
                        boxShadow: "none",
                        transition: "all 0.2s ease-in-out",
                        cursor: "pointer",
                        "&:hover": {
                            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.12)",
                            transform: "translateY(-1px)",
                        },
                    },
                    sizeSmall: {
                        fontSize: "12.7px",
                    },
                    sizeMedium: {
                        fontSize: "13px",
                    },
                    sizeLarge: {
                        fontSize: "14px",
                        padding: "5px 16px",
                    },
                    contained: {
                        color: palette.common.white,
                        "&:hover": {
                            boxShadow: "0px 6px 12px rgba(0, 0, 0, 0.15)",
                        },
                    },
                    containedSecondary: {
                        color: palette.secondary.contrastText,
                        border: `1px solid ${palette.appColors.border}`,
                        "&:hover": {
                            border: `1px solid ${palette.appColors.border2}`,
                            boxShadow: "0px 6px 12px rgba(0, 0, 0, 0.15)",
                        },
                    },
                    outlined: {
                        // Shave 1px off each axis so the 1px border doesn't push outlined buttons 2px
                        // wider / taller than contained / text siblings (which carry no border at all).
                        // Mirrors MUI's own default convention but is required here because the `root`
                        // override above forces a uniform `padding: "4px 14px"` for every variant.
                        padding: "3px 13px",
                        borderWidth: "1px",
                        "&:hover": {
                            borderWidth: "1px",
                        },
                    },
                    outlinedPrimary: {
                        borderColor: palette.primary.main,
                    },
                    outlinedSecondary: {
                        color: palette.text.primary,
                        border: `1px solid ${palette.appColors.border2}`,
                        backgroundColor: palette.appColors.cardBg,
                        "&:hover": {
                            boxShadow: "0px 6px 12px rgba(0, 0, 0, 0.15)",
                        },
                    },
                    outlinedWarning: {
                        color: palette.warning.contrastText,
                        border: `1px solid ${palette.warning.main}`,
                    },
                    text: ({ ownerState, theme }) => ({
                        "&:hover": {
                            boxShadow: "none",
                            transform: "translateY(-1px)",
                            backgroundColor: 
                            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                            ownerState.color && ownerState.color !== "inherit" && theme.palette[ownerState.color]
                                ? alpha(theme.palette[ownerState.color].main, 0.1)
                                : theme.palette.secondary.main,
                        },
                    }),
                },
            },
            MuiIconButton: {
                styleOverrides: {
                    root: ({ ownerState, theme }) => ({
                        cursor: "pointer",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                            backgroundColor: ownerState.color && ownerState.color !== "inherit" && theme.palette[ownerState.color]
                                ? alpha(theme.palette[ownerState.color].main, 0.08)
                                : theme.palette.secondary.main,
                        },
                    }),
                    colorSecondary: {
                        color: palette.text.secondary,
                        "&:hover": {
                            backgroundColor: palette.secondary.main,
                        },
                    },
                    colorError: {
                        "&:hover": {
                            backgroundColor: palette.error.light,
                        },
                    },
                    colorWarning: {
                        "&:hover": {
                            backgroundColor: palette.warning.light,
                        },
                    },
                    colorSuccess: {
                        "&:hover": {
                            backgroundColor: palette.success.light,
                        },
                    },
                    colorInfo: {
                        "&:hover": {
                            backgroundColor: palette.info.light,
                        },
                    },
                    sizeSmall: {
                        fontSize: "14px !important",
                    },
                },
            },
            MuiToggleButton: {
                styleOverrides: {
                    root: {
                        padding: "5px 10px",
                        border: "none",
                        borderTopRightRadius: "8px !important",
                        borderBottomRightRadius: "8px !important",
                        borderTopLeftRadius: "8px !important",
                        borderBottomLeftRadius: "8px !important",
                        textTransform: "none",
                        color: palette.appColors.secondaryFg,
                        "&.Mui-selected": {
                            backgroundColor: palette.appColors.cardBg,
                            color: palette.appColors.primaryFg,
                            "&:hover": {
                                backgroundColor: palette.appColors.cardBg,
                                color: palette.appColors.primaryFg,
                            },
                        },
                        "&:hover": {
                            backgroundColor: alpha(palette.appColors.cardBg, 0.4),
                        },
                    },
                    sizeSmall: {
                        padding: "3px 10px",
                    },
                    sizeLarge: {
                        padding: "8px 10px",
                    },
                },
            },
            MuiToggleButtonGroup: {
                styleOverrides: {
                    root: {
                        gap: "4px",
                        border: "none",
                        padding: 3,
                        backgroundColor: palette.appColors.cardBg2,
                        borderRadius: 10,
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        background: palette.appColors.cardBg,
                        borderRadius: 12,
                        backgroundImage: "none",
                        border: `1px solid ${palette.appColors.border}`,
                    },
                    elevation1: {
                        boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.08)",
                    },
                    elevation2: {
                        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                    },
                    elevation3: {
                        boxShadow: "0px 6px 16px rgba(0, 0, 0, 0.12)",
                    },
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        borderRadius: 12,
                        border: `1px solid ${palette.appColors.border}`,
                        boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.08)",
                        transition: "all 0.2s ease-in-out",
                        backgroundColor: palette.appColors.cardBg,
                        "&:hover": {
                            boxShadow: "0px 6px 16px rgba(0, 0, 0, 0.12)",
                        },
                        "& .MuiCardHeader-subheader": {
                            marginTop: "2px",
                            fontSize: "14px",
                        },
                    },
                },
            },
            MuiTextField: {
                styleOverrides: {
                    root: {
                        "& .MuiOutlinedInput-root": {
                            borderRadius: 8,
                            transition: "all 0.2s ease-in-out",
                            "&:hover:not(.Mui-error)": {
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: palette.primary.main,
                                    borderWidth: "1.5px",
                                },
                            },
                            "&.Mui-focused": {
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderWidth: "2px",
                                },
                            },
                        },
                        "& .MuiOutlinedInput-root .MuiInputBase-input": {
                            padding: "0px",
                            minHeight: "fit-content !important",
                        },
                        "& .MuiInputBase-root.MuiOutlinedInput-root": {
                            padding: "8px 14px",
                        },
                        // "& .MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-sizeSmall": {
                        //   padding: "4px 10px",
                        // },
                        "& .MuiFormLabel-root.MuiInputLabel-root.MuiInputLabel-outlined.MuiInputLabel-sizeMedium": {
                            top: "-7px",
                        },
                        "& .MuiFormLabel-root.MuiInputLabel-root.MuiInputLabel-outlined.MuiInputLabel-shrink": {
                            top: "0px",
                        },
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        transition: "all 0.2s ease-in-out",
                        // height: 38,
                        "&:hover:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
                            borderColor: palette.primary.main,
                            borderWidth: "1.5px",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderWidth: "2px",
                        },
                    },
                    input: {
                        padding: "8px 14px",
                        overflow: "clip !important",
                    },
                },
            },
            MuiInputBase: {
                styleOverrides: {
                    root: {
                        fontSize: "14px !important",
                    },
                },
            },
            MuiCheckbox: {
                styleOverrides: {
                    root: {
                        padding: 4,
                    },
                },
            },
            MuiRadio: {
                styleOverrides: {
                    root: {
                        padding: 4,
                    },
                },
            },
            MuiFormControlLabel: {
                styleOverrides: {
                    root: {
                        marginLeft: "unset",
                    },
                    label: {
                        fontSize: "14px",
                    },
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        color: palette.appColors.secondaryFg,
                        fontWeight: 600,
                        fontSize: "13px !important",
                        top: "4px",
                        "&.MuiInputLabel-outlined.MuiInputLabel-sizeSmall": {
                            top: "0px",
                        },
                        "&.MuiInputLabel-outlined": {
                            top: "-6px",
                        },
                        "&.MuiInputLabel-outlined.MuiInputLabel-shrink": {
                            top: "2px !important",
                        },
                        "&.MuiInputLabel-shrink": {
                            fontSize: "14px !important",
                        },
                    },
                },
            },
            MuiDialog: {
                defaultProps: {
                    scroll: "paper",
                },
                styleOverrides: {
                    paper: {
                        borderRadius: 16,
                        boxShadow: "0px 16px 48px rgba(0, 0, 0, 0.2)",
                    },
                },
            },
            MuiDialogContent: {
                styleOverrides: {
                    root: {
                        paddingTop: "24px",
                        paddingBottom: "24px",
                        paddingLeft: "16px",
                        paddingRight: "16px",
                    },
                },
            },
            MuiDialogTitle: {
                styleOverrides: {
                    root: {
                        fontWeight: 700,
                        padding: "16px",
                    },
                },
            },
            MuiDialogActions: {
                styleOverrides: {
                    root: {
                        padding: "10px 16px",
                        borderTop: `1px solid ${palette.appColors.border}`,
                        backgroundColor: palette.appColors.pageBg,
                        "& > .MuiButton-root:first-of-type:not(:last-of-type)": {
                            backgroundColor: mode === "dark" ? palette.secondary.main : palette.common.white,
                            color: palette.secondary.contrastText,
                            border: `1px solid ${palette.appColors.border}`,
                            borderRadius: 8,
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                                textDecoration: "none",
                                backgroundColor: mode === "dark" ? palette.secondary.dark : palette.secondary.main,
                                border: `1px solid ${palette.appColors.border2}`,
                                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.12)",
                                transform: "translateY(-1px)",
                            },
                            "&.Mui-disabled": {
                                // color: mode === "light" ? "rgba(0, 0, 0, 0.26)" : "rgba(255, 255, 255, 0.3)",
                                // backgroundColor: mode === "light" ? "rgba(0, 0, 0, 0.12)" : "rgba(255, 255, 255, 0.12)",
                                opacity: 0.5,
                                border: "none",
                            },
                        },
                        "& > .MuiButton-root:last-of-type:not(:first-of-type)": {
                            backgroundColor: palette.primary.main,
                            color: palette.common.white,
                            borderRadius: 8,
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                                textDecoration: "none",
                                backgroundColor: palette.primary.dark,
                                boxShadow: "0px 6px 12px rgba(0, 0, 0, 0.15)",
                                transform: "translateY(-1px)",
                            },
                            "&.Mui-disabled": {
                                // color: mode === "light" ? "rgba(0, 0, 0, 0.26)" : "rgba(255, 255, 255, 0.3)",
                                // backgroundColor: mode === "light" ? "rgba(0, 0, 0, 0.12)" : "rgba(255, 255, 255, 0.12)",
                                opacity: 0.5,
                                border: "none",
                            },
                        },
                    },
                },
            },
            MuiTable: {
                styleOverrides: {
                    root: {
                        borderCollapse: "separate",
                        borderSpacing: 0,
                        backgroundColor: palette.appColors.cardBg,
                        border: `1px solid ${palette.appColors.border3}`,
                        borderRadius: 8,
                        overflow: "hidden",
                    },
                },
            },
            MuiTableHead: {
                styleOverrides: {
                    root: {
                        "& .MuiTableCell-head": {
                            backgroundColor: mode === "dark" ? alpha(palette.common.white, 0.035) : palette.appColors.quaternaryFg,
                            borderBottom: `1px solid ${palette.appColors.border}`,
                            fontSize: 12,
                            letterSpacing: "0.05em",
                            fontWeight: 700,
                            color: palette.appColors.secondaryFg,
                            textTransform: "uppercase",
                            paddingTop: 14,
                            paddingBottom: 14,
                        },
                    },
                },
            },
            MuiTableRow: {
                styleOverrides: {
                    root: {
                        transition: "background-color 0.15s ease-in-out",
                        "&:hover": {
                            backgroundColor: mode === "dark" ? alpha(palette.common.white, 0.025) : alpha(palette.common.black, 0.025),
                        },
                    },
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: {
                        paddingTop: 14,
                        paddingBottom: 14,
                        paddingLeft: 16,
                        paddingRight: 16,
                        borderBottom: `1px solid ${palette.appColors.border}`,
                        "&:first-of-type": {
                            paddingLeft: 32,
                        },
                        "&:last-of-type": {
                            paddingRight: 32,
                        },
                        "tbody tr:last-child &": {
                            borderBottom: "none",
                        },
                    },
                },
            },
            // MUIDataTableHeadCell: {
            //   styleOverrides: {
            //     root: {
            //       fontWeight: 700,
            //       backgroundColor: palette.appColors.cardBg2,
            //     },
            //     sortAction: {
            //       fontWeight: 700,
            //     },
            //   },
            // },
            // MUIDataTableBodyCell: {
            //   styleOverrides: {
            //     root: {
            //       overflowY: "hidden",
            //     },
            //   },
            // },
            MuiAlert: {
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        padding: "0px 12px",
                        lineHeight: 1.6,
                    },
                    standardSuccess: {
                        backgroundColor: palette.success.light,
                        color: palette.success.dark,
                        border: `2px solid ${palette.success.light}`,
                        "& .MuiAlert-icon": {
                            color: palette.success.dark,
                        },
                    },
                    standardError: {
                        backgroundColor: palette.error.light,
                        color: palette.error.main,
                        border: `2px solid ${palette.error.light}`,
                        "& .MuiAlert-icon": {
                            color: palette.error.main,
                        },
                    },
                    standardWarning: {
                        backgroundColor: palette.warning.light,
                        color: palette.warning.contrastText,
                        border: `2px solid ${palette.warning.light}`,
                        "& .MuiAlert-icon": {
                            color: palette.warning.contrastText,
                        },
                    },
                    standardInfo: {
                        backgroundColor: palette.info.light,
                        color: palette.info.main,
                        border: `2px solid ${palette.info.light}`,
                        "& .MuiAlert-icon": {
                            color: palette.info.main,
                        },
                    },
                    outlined: {
                        border: `1px solid ${palette.appColors.border}`,
                    },
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        borderRadius: 6,
                        fontSize: "0.8rem",
                        fontWeight: 700,
                        lineHeight: 1,
                        padding: "4px 8px",
                        height: 26,
                        gap: 4,
                        "& .MuiChip-label": {
                            padding: 0,
                        },
                        "& .MuiSvgIcon-root": {
                            margin: "unset",
                        },
                    },
                    sizeSmall: {
                        fontSize: "0.7rem",
                        padding: "4px 6px",
                        height: 22,
                    },
                    colorPrimary: {
                        "&.MuiChip-filled": {
                            color: palette.common.white,
                        },
                        "&.MuiChip-outlined": {
                            backgroundColor: alpha(palette.primary.main, 0.08),
                        },
                    },
                    colorDefault: {
                        "&.MuiChip-outlined": {
                            color: palette.secondary.contrastText,
                            backgroundColor: alpha(palette.secondary.main, 0.75),
                            border: `1px solid ${palette.appColors.border2}`,
                        },
                    },
                    colorSecondary: {
                        "&.MuiChip-filled": {
                            color: palette.secondary.contrastText,
                        },
                        "&.MuiChip-outlined": {
                            color: palette.secondary.contrastText,
                            backgroundColor: alpha(palette.secondary.main, 0.75),
                            border: `1px solid ${palette.appColors.border2}`,
                        },
                    },
                    colorError: {
                        "&.MuiChip-filled": {
                            color: palette.common.white,
                        },
                        "&.MuiChip-outlined": {
                            backgroundColor: palette.error.light,
                        },
                    },
                    colorSuccess: {
                        "&.MuiChip-filled": {
                            color: palette.common.white,
                        },
                        "&.MuiChip-outlined": {
                            color: palette.success.dark,
                            backgroundColor: palette.success.light,
                        },
                    },
                    colorWarning: {
                        "&.MuiChip-filled": {
                            color: palette.common.white,
                        },
                        "&.MuiChip-outlined": {
                            color: palette.warning.contrastText,
                            backgroundColor: palette.warning.light,
                            border: `1px solid ${palette.warning.contrastText}`,
                        },
                    },
                    colorInfo: {
                        "&.MuiChip-filled": {
                            color: palette.common.white,
                        },
                        "&.MuiChip-outlined": {
                            backgroundColor: palette.info.light,
                        },
                    },
                },
            },
            MuiBadge: {
                styleOverrides: {
                    badge: {
                        fontWeight: 800,
                        lineHeight: 1,
                        color: palette.common.white,
                        height: 16,
                        minWidth: 16,
                        padding: 5,
                    },
                    colorSecondary: {
                        color: palette.secondary.contrastText + "!important",
                    },
                },
            },
            MuiAvatar: {
                styleOverrides: {
                    root: {
                        border: `2px solid ${palette.appColors.cardBg}`,
                        backgroundColor: `${palette.appColors.cardBg2}`,
                        color: "inherit",
                    },
                },
            },
            MuiAvatarGroup: {
                styleOverrides: {
                    avatar: {
                        border: `2px solid ${palette.appColors.cardBg}`,
                        backgroundColor: `${palette.appColors.cardBg2}`,
                        color: "inherit",
                    },
                },
            },
            MuiListItemAvatar: {
                styleOverrides: {
                    root: {
                        minWidth: "36px",
                    },
                },
            },
            MuiSwitch: {
                styleOverrides: {
                    root: {
                        width: 36,
                        height: 20,
                        padding: 0,
                        borderRadius: 20,
                        marginRight: "6px",
                        "& .MuiFormControlLabel-root": {
                            marginLeft: "unset",
                        },
                        "& .MuiSwitch-switchBase": {
                            padding: 0,
                            margin: "2px 0 0 2px",
                            transitionDuration: "300ms",
                            "& .MuiSwitch-input": {
                                left: 0,
                                width: "100%",
                            },
                            "&.Mui-checked": {
                                transform: "translateX(16px)",
                                color: "#fff",
                                "& + .MuiSwitch-track": {
                                    backgroundColor: palette.primary.main,
                                    opacity: 1,
                                    border: 0,
                                },
                            },
                            "&.Mui-focusVisible .MuiSwitch-thumb": {
                                color: palette.primary.main,
                                border: "6px solid #fff",
                            },
                        },
                        "& .MuiSwitch-thumb": {
                            boxSizing: "border-box",
                            width: 16,
                            height: 16,
                        },
                        "& .MuiSwitch-track": {
                            backgroundColor: palette.appColors.tertiaryFg,
                            opacity: mode === "dark" ? 0.2 : 1,
                            transition: "background-color 300ms",
                        },
                        "& .MuiSwitch-switchBase.Mui-disabled .MuiSwitch-thumb": {
                            color: "#fff",
                            opacity: mode === "dark" ? "0.5 !important" : "0.75 !important",
                        },
                        "& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track": {
                            opacity: mode === "dark" ? "0.2 !important" : "0.4 !important",
                        },
                    },
                },
            },
            MuiDrawer: {
                styleOverrides: {
                    paper: {
                        zIndex: 1000,
                        background: palette.appColors.cardBg,
                        "& .MuiListItem-root": {
                            paddingTop: 0,
                            paddingBottom: 0,
                        },
                    },
                },
            },
            MuiContainer: {
                styleOverrides: {
                    maxWidthLg: {
                        maxWidth: "1280px !important",
                    },
                    maxWidthMd: {
                        maxWidth: "960px !important",
                    },
                },
            },
            MuiListSubheader: {
                styleOverrides: {
                    root: {
                        backgroundColor: "inherit",
                    },
                },
            },
            MuiMenu: {
                styleOverrides: {
                    paper: {
                        borderRadius: 8,
                        marginTop: 4,
                        border: `1px solid ${palette.appColors.border}`,
                    },
                    list: {
                        display: "flex",
                        flexDirection: "column",
                        gap: "2px",
                        padding: "4px !important",
                        maxHeight: "calc(100% - 96px)",
                        overflowY: "auto",
                        "&::-webkit-scrollbar": {
                            width: 12,
                        },
                        "&::-webkit-scrollbar-track": {
                            backgroundColor: "transparent",
                            margin: "4px 0",
                        },
                        "&::-webkit-scrollbar-thumb": {
                            backgroundColor: `${palette.appColors.border}`,
                            borderRadius: 12,
                            border: "2px solid transparent",
                            backgroundClip: "padding-box",
                            "&:hover": {
                                backgroundColor: palette.appColors.border2,
                            },
                        },
                    },
                },
            },
            MuiMenuItem: {
                styleOverrides: {
                    root: {
                        borderRadius: 6,
                        height: "34px",
                    },
                },
            },
            MuiDivider: {
                styleOverrides: {
                    root: {
                        margin: "2px 0",
                    },
                },
            },
            MuiSelect: {
                styleOverrides: {
                    select: {
                        borderRadius: 8,
                    },
                },
            },
            MuiPopover: {
                styleOverrides: {
                    // root: {
                    //   maxHeight: "calc(100% - 96px)",
                    // },
                    paper: {
                        borderRadius: 8,
                        border: `1px solid ${palette.appColors.border}`,
                        "& ::webkit-scrollbar": {
                            display: "none",
                        },
                        scrollbarWidth: "none",
                    },
                },
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        fontSize: "12px",
                        lineHeight: 1.3,
                        fontWeight: 700,
                        backgroundColor: palette.appColors.primaryFg,
                        color: palette.appColors.cardBg,
                        borderRadius: 8,
                        padding: "6px 12px",
                        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
                        backdropFilter: "blur(4px)",
                    },
                    arrow: {
                        color: palette.appColors.primaryFg,
                    },
                },
            },
            MuiSnackbar: {
                styleOverrides: {
                    root: {
                        "& .notistack-MuiContent-success": {
                            backgroundColor: palette.success.light,
                            color: palette.success.main,
                            fontWeight: 500,
                        },
                        "& .notistack-MuiContent-error": {
                            backgroundColor: palette.error.light,
                            color: palette.error.main,
                            fontWeight: 500,
                        },
                        "& .notistack-MuiContent-warning": {
                            backgroundColor: palette.warning.light,
                            color: palette.warning.contrastText,
                            fontWeight: 500,
                        },
                        "& .notistack-MuiContent-info": {
                            backgroundColor: palette.info.light,
                            color: palette.info.main,
                            fontWeight: 500,
                        },
                        "& .notistack-MuiContent-default": {
                            backgroundColor: alpha(palette.secondary.main, 0.08),
                            color: palette.secondary.main,
                            fontWeight: 500,
                        },
                    },
                },
            },
            MuiDateCalendar: {
                styleOverrides: {
                    root: {
                        height: "unset",
                        "& .MuiPickersDay-root": {
                            fontSize: "14px",
                        },
                    },
                },
            },
            MuiPickersYear: {
                styleOverrides: {
                    yearButton: {
                        "&:hover": {
                            backgroundColor: `${palette.secondary.dark} !important`,
                        },
                        "&.Mui-selected": {
                            backgroundColor: `${palette.primary.main} !important`,
                            color: `${palette.common.white} !important`,
                            "&:hover": {
                                backgroundColor: `${palette.primary.main} !important`,
                            },
                        },
                    },
                },
            },
            MuiPickersMonth: {
                styleOverrides: {
                    monthButton: {
                        "&:hover": {
                            backgroundColor: `${palette.secondary.dark} !important`,
                        },
                        "&.Mui-selected": {
                            backgroundColor: `${palette.primary.main} !important`,
                            color: `${palette.common.white} !important`,
                            "&:hover": {
                                backgroundColor: `${palette.primary.main} !important`,
                            },
                        },
                    },
                },
            },
            MuiPickersDay: {
                styleOverrides: {
                    root: {
                        fontSize: "14px",
                        "&:hover": {
                            backgroundColor: `${palette.secondary.dark} !important`,
                        },
                        "&.MuiPickersDay-today": {
                            backgroundColor: `${palette.secondary.main} !important`,
                            border: `1px solid ${palette.secondary.dark} !important`,
                        },
                        "&.Mui-selected": {
                            backgroundColor: `${palette.primary.main} !important`,
                            color: `${palette.common.white} !important`,
                            "&:hover": {
                                backgroundColor: `${palette.primary.main} !important`,
                            },
                        },
                    },
                },
            },
            MuiStepper: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        marginTop: 8,
                        marginBottom: 20,
                        transition: "all 0.3s ease-in-out",
                        "& .MuiStepLabel-label": {
                            color: palette.text.primary,
                            fontSize: "0.875rem",
                            fontWeight: 500,
                            [theme.breakpoints.down("sm")]: {
                                display: "none", // Hide labels on mobile
                            },
                            "&.Mui-active": {
                                fontWeight: 600,
                            },
                        },
                        "& .MuiStep-root": {
                            [theme.breakpoints.down("sm")]: {
                                paddingLeft: 4,
                                paddingRight: 4,
                            },
                        },
                        "& .MuiStepIcon-root": {
                            color: palette.appColors.tertiaryFg,
                            overflow: "visible",
                            transition: "all 0.2s ease-in-out",
                            "& .MuiStepIcon-text": {
                                fill: palette.common.white,
                                fontSize: ".9rem",
                            },
                        },
                        "& .MuiStepIcon-root.Mui-active": {
                            color: palette.primary.main,
                            transform: "scale(1.15)",
                        },
                        "& .MuiStepIcon-root.Mui-completed": {
                            color: palette.primary.main,
                        },
                        "& .MuiStepConnector-line": {
                            borderColor: palette.appColors.border2,
                        },
                    }),
                },
            },
        }, typography: {
            fontFamily: `'${AppConfig.fonts.primary}', Helvetica, Arial, sans-serif`,
            fontWeightLight: 300,
            fontWeightRegular: 400,
            fontWeightMedium: 500,
            fontWeightBold: 700,
            fontSize: 12,
            h1: { lineHeight: 1.2, letterSpacing: "-0.005em", fontWeight: 700 },
            h2: { lineHeight: 1.2, letterSpacing: "-0.005em", fontWeight: 700 },
            h3: { lineHeight: 1.2, letterSpacing: "-0.005em", fontWeight: 700 },
            h4: { lineHeight: 1.2, letterSpacing: "-0.005em", fontWeight: 700 },
            h5: { lineHeight: 1.2, letterSpacing: "-0.005em", fontWeight: 700 },
            h6: { lineHeight: 1.2, letterSpacing: "-0.005em", fontWeight: 700 },
            subtitle1: { lineHeight: 1.2, letterSpacing: "-0.005em", fontWeight: 600 },
            subtitle2: { fontSize: "16px", lineHeight: 1.3, fontWeight: 700, letterSpacing: "-0.005em" },
            body1: { fontSize: "15px", lineHeight: 1.2, fontWeight: 600, letterSpacing: "-0.005em" },
            body2: { fontSize: "14px", lineHeight: 1.3, fontWeight: 600, letterSpacing: "-0.005em" },
            caption: { fontSize: "12px", lineHeight: 1.2, fontWeight: 700, letterSpacing: "-0.005em" },
            overline: { lineHeight: 1, letterSpacing: "-0.005em", fontWeight: 600 },
            button: { lineHeight: 1.7, fontWeight: 700, letterSpacing: "-0.005em" },
        } }));
};
// Pre-create both themes for better performance
const lightTheme = createThemeWithComponents("light");
const darkTheme = createThemeWithComponents("dark");
// Export function to get pre-created theme
const createAppTheme = (mode) => (mode === "light" ? lightTheme : darkTheme);
export { getDesignTokens, createAppTheme, lightTheme, darkTheme };
export default lightTheme;
